import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Alert, Button, Card, Form, Input, Result, Skeleton, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { activateInvite, errorText, getInvite } from "./api";
import { isNative, setTenantDomain } from "../../../native/platform";
import "./portal.css";

const { Paragraph, Title } = Typography;

// Same rule as the server (utils/portal/calc#passwordProblem)
const strongEnough = value =>
  value.length >= 8 &&
  /[A-Z]/.test(value) &&
  /[a-z]/.test(value) &&
  /\d/.test(value) &&
  /[!@#$%^&*(),.?":{}|<>]/.test(value);

/** The invitation link: the customer sets their password, then signs in as usual. */
const ActivatePortal = () => {
  const { t, i18n } = useTranslation("dashboard");
  const { token } = useParams();
  const domain = new URLSearchParams(useLocation().search).get("domain") || "";
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    getInvite(token, domain)
      .then(res => setState({ loading: false, invite: res.data.data }))
      .catch(() => setState({ loading: false, failed: true }));
  }, [token, domain]);

  const submit = async values => {
    setBusy(true);
    setError("");
    try {
      await activateInvite(token, domain, values);
      // The app signs in against the company's address from the link
      if (isNative && domain) setTenantDomain(domain);
      setDone(true);
    } catch (err) {
      setError(errorText(err, t("The password could not be set. Please try again.")));
    } finally {
      setBusy(false);
    }
  };

  const invite = state.invite;
  return (
    <div className="portal-page portal-public" dir={i18n.dir?.()}>
      <div className="portal-public__inner">
        {state.loading ? (
          <Card>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        ) : state.failed ? (
          <Card>
            <Result
              status="warning"
              title={t("This link is not valid or has expired.")}
              subTitle={t("Please ask the company for a new invitation.")}
            />
          </Card>
        ) : done ? (
          <Card>
            <Result
              status="success"
              title={t("Your password is set.")}
              subTitle={t("Sign in with your email address {{email}}.", { email: invite.email })}
              extra={
                <Link to="/login">
                  <Button type="primary" size="large">
                    {t("Sign in")}
                  </Button>
                </Link>
              }
            />
          </Card>
        ) : (
          <Card>
            {invite.company.logo ? (
              <img className="portal-public__logo" src={invite.company.logo} alt="" />
            ) : null}
            <Title level={3}>{t("Welcome to the customer portal")}</Title>
            <Paragraph>
              {t(
                "{{company}} invites you to its customer portal. Please choose a password for {{email}}.",
                {
                  company: invite.company.name,
                  email: invite.email
                }
              )}
            </Paragraph>
            {error && <Alert className="portal-gap" type="error" showIcon message={error} />}
            <Form layout="vertical" onFinish={submit} requiredMark={false}>
              <Form.Item
                name="password"
                label={t("Password")}
                extra={t(
                  "At least 8 characters with upper and lower case letters, a digit and a special character."
                )}
                rules={[
                  {
                    validator: (_, value) =>
                      strongEnough(String(value || ""))
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("The password is not strong enough.")))
                  }
                ]}
              >
                <Input.Password size="large" autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                name="repeatPassword"
                label={t("Repeat password")}
                dependencies={["password"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: (_, value) =>
                      value && value === getFieldValue("password")
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("The passwords do not match.")))
                  })
                ]}
              >
                <Input.Password size="large" autoComplete="new-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" size="large" block loading={busy}>
                {t("Set password")}
              </Button>
            </Form>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ActivatePortal;
